import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CultureStepContent from './steps/CultureStepContent';
import CulturalPracticeStepContent from './steps/CulturalPracticeStepContent';
import TreatmentTypeStepContent from './steps/TreatmentTypeStepContent';
import EnemiesStepContent from './steps/EnemiesStepContent';
import InterventionPeriodStepContent from './steps/InterventionPeriodStepContent';
import ApplicationTypeStepContent from './steps/ApplicationTypeStepContent';
import { MULTICHOIX_SEARCH_STEPS } from '../../constants/multichoixSteps';

const ANIMATION_CONFIG = {
  initial: false,
  animate: { height: 'auto', opacity: 1 },
  animateHidden: { height: 0, opacity: 0 },
  transition: { duration: 0.3 },
};

const STEP_COMPONENTS = {
  1: CultureStepContent,
  2: CulturalPracticeStepContent,
  3: TreatmentTypeStepContent,
  4: EnemiesStepContent,
  5: InterventionPeriodStepContent,
  6: ApplicationTypeStepContent,
};

const MultichoixStepContent = ({
  step,
  isActive,
  value,
  isValid,
  isRequired,
  allSelections,
  allLabels,
  onChange,
  onNext,
  onSkip,
  onAutoSearch,
  onForceRevalidation,
  selectionLabels,
  mode,
}) => {
  const { t } = useTranslation('treatment-search');
  const StepComponent = STEP_COMPONENTS[step.key];

  const isLastStep = step.key >= MULTICHOIX_SEARCH_STEPS.length;

  // SP-430: stable callbacks so the per-step "auto-select single option" hook
  // doesn't re-run on every render. onAutoAdvance is omitted on the last step
  // (there is no next criterion to advance to).
  // SP-430 M11: on the LAST step (Type d'application), selecting the criterion
  // auto-launches the search. Extraction keeps its own flow (multi-select +
  // Excel export), so it is excluded here.
  const handleStepComponentChange = useCallback(
    (values, labels) => {
      onChange(step.key, values, labels);
      if (isLastStep && mode !== 'extraction' && values && values.length > 0) {
        onAutoSearch?.(step.key, values, labels);
      }
    },
    [onChange, step.key, isLastStep, mode, onAutoSearch]
  );
  const handleAutoAdvance = useCallback(
    () => onNext(step.key + 1),
    [onNext, step.key]
  );

  if (!StepComponent) {
    return null;
  }

  const hasSelections = value?.length > 0;
  const canProceed = isValid && step.key < MULTICHOIX_SEARCH_STEPS.length;

  const renderActionButtons = () => (
    <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0">
      {hasSelections && canProceed && (
        <button
          onClick={() => onNext(step.key + 1)}
          className="w-full sm:w-auto text-sm text-green-700 hover:text-green-800 font-medium py-2 sm:py-0 px-3 sm:px-0 rounded sm:rounded-none bg-green-50 sm:bg-transparent hover:bg-green-100 sm:hover:bg-transparent text-center order-1"
        >
          {t('multichoix.actions.continue', 'Continuer vers l\'étape {{step}} →', { step: step.key + 1 })}
        </button>
      )}
    </div>
  );

  // Only render step content when active to prevent unnecessary API calls
  if (!isActive) {
    return null;
  }

  return (
    <motion.div
      {...ANIMATION_CONFIG}
      animate={ANIMATION_CONFIG.animate}
      className="overflow-hidden"
    >
      <div className="p-6 bg-[#f8fdec] rounded-b-[16px]">
        {step.description && (
          <p className="text-gray-600 mb-4">{t(step.description)}</p>
        )}
        <StepComponent
          step={step}
          value={value}
          selectionLabels={selectionLabels}
          allSelections={allSelections}
          allLabels={allLabels}
          onChange={handleStepComponentChange}
          onAutoAdvance={isLastStep ? undefined : handleAutoAdvance}
          onForceRevalidation={onForceRevalidation}
          mode={mode}
        />
        {renderActionButtons()}
      </div>
    </motion.div>
  );
};

export default MultichoixStepContent;
